<template>
    <div class="ppv2-relative" ref="root">
        <!-- Trigger -->
        <slot name="trigger">
            <button
                @click="toggleDropdown"
                class="ppv2-min-h-[40px] ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                :aria-expanded="isOpen"
                aria-haspopup="menu"
                :disabled="disabled || loading"
            >
                <span v-if="loading" class="loader ppv2-mr-2"></span>
                <svg
                    v-else
                    width="17"
                    height="17"
                    viewBox="0 0 30 30"
                    :fill="isDark ? '#fff' : '#000'"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.21289 17.6012C1.21289 14.7752 2.69884 12.2962 4.93205 10.9029C5.53943 6.12426 9.62017 2.42969 14.5638 2.42969C19.5074 2.42969 23.5882 6.12426 24.1956 10.9029C26.4288 12.2962 27.9148 14.7752 27.9148 17.6012C27.9148 21.7541 24.7059 25.1575 20.6324 25.4675L8.49522 25.4904C4.42171 25.1575 1.21289 21.7541 1.21289 17.6012ZM20.4483 23.0469C23.2804 22.8315 25.4873 20.4609 25.4873 17.6012C25.4873 15.692 24.5023 13.9554 22.9107 12.9625L21.9328 12.3524L21.7875 11.209C21.3285 7.59775 18.24 4.85713 14.5638 4.85713C10.8877 4.85713 7.79912 7.59775 7.34012 11.209L7.19479 12.3524L6.21692 12.9625C4.62534 13.9554 3.64034 15.692 3.64034 17.6012C3.64034 20.4609 5.84728 22.8315 8.67935 23.0469L8.88968 23.063H20.238L20.4483 23.0469ZM15.7776 14.5669H19.4187L14.5638 20.6355L9.70895 14.5669H13.3501V9.71202H15.7776V14.5669Z"
                        :fill="isDark ? '#fff' : '#000'"
                    />
                </svg>

                <span
                    class="poppinsLight"
                    :class="{ 'ppv2-hidden md:ppv2-inline': hideLabelOnMobile }"
                    :style="{ fontSize: normalizeFontSize(15) }"
                    >{{ label }}</span
                >
                <span
                    v-if="badge"
                    class="ppv2-text-xs ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full ppv2-bg-[#762EB9] ppv2-text-white ppv2-flex ppv2-items-center ppv2-gap-1"
                >
                    <StarIcon size="12" class="ppv2-fill-white" /> {{ badge }}
                </span>
            </button>
        </slot>

        <!-- Menu -->
        <transition name="fade-scale">
            <div
                v-if="isOpen"
                class="menuDropdown ppv2-w-full ppv2-absolute ppv2-overflow-hidden"
                :class="dropdownClasses"
                role="menu"
                ref="menu"
                tabindex="-1"
                @keydown.prevent.stop="onMenuKeydown"
            >
                <button
                    v-for="(option, i) in filteredOptions"
                    :key="i"
                    class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-1 poppinsLight ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-rounded-md"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    role="menuitem"
                    :disabled="option.disabled || option.loading"
                    :class="{
                        'ppv2-opacity-40 ppv2-cursor-not-allowed': option.disabled || option.loading,
                        'ppv2-bg-[#272c54] ppv2-text-white': focusedIndex === i,
                    }"
                    @mousemove="focusedIndex = i"
                    @mouseleave="focusedIndex = -1"
                    @click="selectOption(option)"
                >
                    <span v-if="option.loading" class="loader"></span>
                    {{ option.label }}
                </button>

                <!-- Empty -->
                <div v-if="!options || options.length === 0" class="ppv2-px-4 ppv2-py-3 ppv2-text-sm ppv2-opacity-50">
                    {{ $t('common.no_results') }}
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { CloudDownload } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'
import { StarIcon } from 'vue-feather-icons'
export default {
    name: 'ExportSelect',
    components: { CloudDownload, StarIcon },

    props: {
        label: { type: String, default: 'Export' },
        hideLabelOnMobile: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        align: { type: String, default: 'right' }, // left | right
        position: { type: String, default: 'bottom' }, // bottom | top
        autoFlip: { type: Boolean, default: true },
        options: {
            type: Array,
            default: () => [
                { label: 'CSV', value: 'csv' },
                { label: 'PDF', value: 'pdf' },
            ],
        },
        use_only_csv: {
            type: Boolean,
            default: false,
        },
        useOnlyCsv: {
            type: Boolean,
            default: false,
        },
        badge: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            isOpen: false,
            focusedIndex: -1,
            computedPos: null,
        }
    },

    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        effectivePosition() {
            return this.computedPos || this.position
        },

        dropdownClasses() {
            return [
                this.align === 'left' ? 'ppv2-left-0' : 'ppv2-right-0',
                this.effectivePosition === 'top' ? 'ppv2-bottom-full ppv2-mb-2' : 'ppv2-top-full ppv2-mt-2',
            ]
        },
        filteredOptions() {
            if (this.use_only_csv || this.useOnlyCsv) {
                return [{ label: 'CSV', value: 'csv' }]
            }
            return this.options
        },
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('resize', this.updatePosition)
        window.addEventListener('scroll', this.updatePosition, true)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('resize', this.updatePosition)
        window.removeEventListener('scroll', this.updatePosition, true)
    },

    methods: {
        normalizeFontSize,
        normalizeSize,
        toggleDropdown() {
            if (this.disabled) return

            if (this.use_only_csv || this.useOnlyCsv) {
                this.$emit('export-selected', 'csv')
                return
            }

            const ready = (this.options || []).filter((o) => !o.disabled && !o.loading)
            if (ready.length === 1) {
                this.$emit('export-selected', ready[0].value)
                return
            }

            this.isOpen = !this.isOpen

            if (this.isOpen) {
                this.$nextTick(() => {
                    if (this.autoFlip) this.computeAutoFlip()
                    this.focusFirstItem()
                    this.$refs.menu.focus()
                })
            }
        },

        closeDropdown() {
            this.isOpen = false
            this.focusedIndex = -1
            this.computedPos = null
        },

        handleClickOutside(event) {
            if (!this.$refs.root.contains(event.target)) this.closeDropdown()
        },

        computeAutoFlip() {
            const btn = this.$refs.root.getBoundingClientRect()
            const menu = this.$refs.menu.offsetHeight

            const spaceBelow = window.innerHeight - btn.bottom
            const spaceAbove = btn.top

            if (this.position === 'bottom') {
                this.computedPos = spaceBelow >= menu ? 'bottom' : 'top'
            } else {
                this.computedPos = spaceAbove >= menu ? 'top' : 'bottom'
            }
        },

        updatePosition() {
            if (this.isOpen && this.autoFlip) this.$nextTick(this.computeAutoFlip)
        },

        onMenuKeydown(e) {
            if (e.key === 'Escape') return this.closeDropdown()

            const max = this.options.length - 1

            if (e.key === 'ArrowDown') {
                this.focusedIndex = (this.focusedIndex + 1) % (max + 1)
            } else if (e.key === 'ArrowUp') {
                this.focusedIndex = (this.focusedIndex - 1 + max + 1) % (max + 1)
            } else if (e.key === 'Enter') {
                const opt = this.options[this.focusedIndex]
                if (opt && !opt.disabled) this.selectOption(opt)
            }
        },

        focusFirstItem() {
            const idx = this.options.findIndex((o) => !o.disabled)
            this.focusedIndex = idx >= 0 ? idx : -1
        },

        selectOption(option) {
            if (option.disabled || option.loading) return
            this.$emit('export-selected', option.value)
            this.closeDropdown()
        },
    },
}
</script>

<style scoped lang="scss">
.loader {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.menuDropdown {
    z-index: 14;
}

/* Transition */
.fade-scale-enter-active,
.fade-scale-leave-active {
    transition: all 150ms ease-out;
}

.fade-scale-enter {
    opacity: 0;
    transform: scale(0.95);
}

.fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
</style>
